import { Alert } from '@cmsgov/ds-medicare-gov';
import { MouseEventHandler, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '../../common/components/button';
import Div from '../../common/components/div';
import H1 from '../../common/components/h1';
import MedicareNumberDialog from '../../common/components/MedicareNumberDialog';
import StackingButtons from '../../common/components/StackingButtons';
import StepHeader from '../../common/components/StepHeader';
import TranslatedSpan from '../../common/components/TranslatedSpan';
import capitalizeWords from '../../common/functions/capitalizeWords';
import { isValidMedicareNumber } from '../../common/functions/isValidMedicareNumber';
import { isValidYear } from '../../common/functions/isValidYear';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart, usePageViewWithSteps } from '../../common/hooks/use.analytics';
import { usePageTitle } from '../../common/hooks/usePageTitle';
import dateFieldValue, { DateObject } from '../../common/objects/models/date.field.value';
import NoAccountInRecords from '../../find-my-account/components/NoAccountInRecords';
import MedicareNumberSection from '../../common/components/MedicareNumberSection';
import LastNameSection from '../../common/components/LastNameSection';
import CancelButton from '../../common/components/CancelButton';
import { AllFieldsRequired } from '../../common/components/AllFieldsRequired';
import useVerifyAccountApi, { VerifyAccountResponse } from '../../common/hooks/useVerifyAccountApi';
import { ForgotUsernameFields, SmartErrorAlert } from '../../common/components/SmartErrorAlert';
import { AxiosResponse } from 'axios';
import { InvalidField } from '../../common/functions/errorAlertContent/getAlertContent';
import { DateFields2 } from '../../common/components/DateFields2';

interface ForgotUsernameProps {
    initData?: {
        dateOfBirth?: DateObject
        lastName?: string
        medicareNumber?: string
        suffix?: string
        username?: string
    }
    buttonText?: string
    combinedWithPassword?: boolean
    titleText?: string
    onData?: (data: {
        lastName: string
        suffix: string
        dateOfBirth: DateObject
        medicareNumber: string
        username: string
    }) => void
    onNextClick: MouseEventHandler
    onNextLoading?: boolean
    subTitle?: string
    getSecretQuestionErrorMessage?: string | JSX.Element | null
}

type FieldErrState = 'empty' | 'invalid' | null;
const ForgotUsername = (props: ForgotUsernameProps) => {
    const initData = props.initData || {};

    const [lastNameRef, setLastNameRef] = useState<HTMLElement | null>(null);
    const [medicareNumberRef, setMedicareNumberRef] = useState<HTMLElement | null>(null);
    const [monthRef, setMonthRef] = useState<HTMLElement | null>(null);
    const [dateOfBirth, setDateOfBirth] = useState(initData.dateOfBirth || dateFieldValue());
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const history = useHistory();
    const [lastName, setLastName] = useState(initData.lastName || '');
    const [medicareNumber, setMedicareNumber] = useState(initData.medicareNumber || '');
    const [isNotFound, setIsNotFound] = useState(false);
    const [suffix, setSuffix] = useState(initData.suffix || '');
    const [username, setUsername] = useState<string | null>(initData.username || '');
    const [isLoading, setIsLoading] = useState(false);

    const [apiResponse, setApiResponse] = useState<AxiosResponse<VerifyAccountResponse> | null>(null);

    const formStart = useFormStart('username_reminder');
    const analyticsComponentName = props.combinedWithPassword ? 'forgot username and password' : 'username_reminder';
    const conversion = useConversion(analyticsComponentName, 'username_reminder');
    usePageTitle(props.titleText || translate('get_username'));
    const isIntermediateConversionAttempt = props.combinedWithPassword;


    const [invalidFields, setInvalidFields] = useState<InvalidField<ForgotUsernameFields>[]>([]);

    const [mbiErrState, setMbiErrState] = useState<FieldErrState>(null);
    const [lastNameErrState, setLastNameErrState] = useState<FieldErrState>(null);
    const [dateOfBirthErrState, setDateOfBirthErrState] = useState<FieldErrState>(null);

    const [mbiTouched, setMbiTouched] = useState(false);
    const [lastNameTouched, setLastNameTouched] = useState(false);
    const [dateOfBirthTouched, setDateOfBirthTouched] = useState(false);

    const [submitAttempted, setSubmitAttempted] = useState(false);

    const api = useVerifyAccountApi();

    const callVerifyUser = async () => {
        setApiResponse(null);
        const lastNameWithSuffix = suffix ? `${lastName} ${suffix}` : lastName;
        const response = await api.post(medicareNumber, lastNameWithSuffix, dateOfBirth);
        setApiResponse(response);
        setIsLoading(false);

        const newUsername = response?.data.username ?? null;
        setUsername(newUsername);

        if (newUsername) {
            if (!isIntermediateConversionAttempt) {
                conversion.success();
            }
        } else if (response?.status === 404) {
            setIsNotFound(true);
        }

        // for forgot username and password flow - pass data on to reset password
        if (newUsername && props.onData) {
            props.onData({
                lastName,
                suffix,
                dateOfBirth,
                medicareNumber,
                username: newUsername,
            });
        }
    };

    const getBackButton = () => {
        return <>
            <Button
                onClick={() => setUsername('')}
            >
                <TranslatedSpan english={'back'}/>
            </Button>
        </>;
    };

    const getGetUsernameButton = () => {
        const buttonText = props.buttonText || capitalizeWords(translate('get_username'));
        return <>
            <Button
                variation={'solid'}
                mfaAnalyticsData={{ text: buttonText, parentComponentHeading: header}}
                onClick={handleGetUsernameButtonClick}
                isLoading={isLoading}
            >
                {buttonText}
            </Button>
        </>;
    };

    const getNextButton = () => {
        const buttonText = translate('next');
        return <>
            <Button
                variation={'solid'}
                mfaAnalyticsData={{
                    text: buttonText,
                    parentComponentHeading: header
                }}
                onClick={props.onNextClick}
                isLoading={props.onNextLoading}
            >
                {buttonText}
            </Button>
        </>;
    };

    const getYourUsernameButtons = () => {
        const buttonText = translate('back_to_login');
        if (props.combinedWithPassword)
            return <>
                <StackingButtons
                    button1={getBackButton()}
                    button2={getNextButton()}
                />
            </>;
        else
            return <>
                <Div isRow={true} paddingBottom={3} paddingTop={2} style={{paddingLeft: 16}}>
                    <Button
                        variation={'solid'}
                        mfaAnalyticsData={{
                            text: buttonText,
                            parentComponentHeading: header
                        }}
                        onClick={() => history.push('/login')}
                    >
                        {buttonText}
                    </Button>
                </Div>
            </>;
    };

    const handleGetUsernameButtonClick = () => {
        setSubmitAttempted(true);
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        conversion.attempt();

        const newInvalidFields: InvalidField<ForgotUsernameFields>[] = [];
        if (mbiErrState) {
            newInvalidFields.push({ name: 'mbi', type: mbiErrState });
        }
        if (lastNameErrState) {
            newInvalidFields.push({ name: 'lastName', type: lastNameErrState });
        }
        if (dateOfBirthErrState) {
            newInvalidFields.push({ name: 'dateOfBirth', type: dateOfBirthErrState });
        }

        setInvalidFields(newInvalidFields);
        if (newInvalidFields.length) {
            setIsLoading(false);
            return;
        }

        callVerifyUser();
    };

    useEffect(() => {
        const { month, day, year } = dateOfBirth;
        const isEmpty = !month && !day && !year;
        const isInvalid = !isValidYear(year);

        if (isEmpty) {
            setDateOfBirthErrState('empty');
        } else if (isInvalid) {
            setDateOfBirthErrState('invalid');
        } else {
            setDateOfBirthErrState(null);
        }
    }, [dateOfBirth]);

    const updateDateOfBirth = (dateObject: DateObject) => {
        formStart();
        setDateOfBirth(dateObject);
    };

    useEffect(() => {
        setLastNameErrState(lastName.length ? null : 'empty');
    }, [lastName]);

    const updateLastName = (value: string) => {
        formStart();
        const newValue = value.trimStart();
        setLastName(newValue);
    };

    useEffect(() => {
        const isValid = isValidMedicareNumber(medicareNumber);
        const isEmpty = !medicareNumber;
        if (isValid) {
            setMbiErrState(null);
        } else if (isEmpty) {
            setMbiErrState('empty');
        } else {
            setMbiErrState('invalid');
        }
    }, [medicareNumber]);

    const updateMedicareNumber = (value: string) => {
        formStart();
        const newValue = value.trim();
        setMedicareNumber(newValue);
    };

    const getMedicareErrorMessage = () => {
        if (!mbiTouched && !submitAttempted) {
            return null;
        }
        if (mbiErrState === 'empty') {
            return translate('enter_your_medicare_number');
        }
        if (mbiErrState === 'invalid') {
            return translate('check_mbi_and_try_again')
        }
        return null;
    }

    const getLastNameErrorMessage = () => {
        if (!lastNameTouched && !submitAttempted) {
            return null;
        }
        return lastNameErrState ? translate('enter_your_last_name') : null;
    }

    const getDateOfBirthErrMessage = () => {
        if (!dateOfBirthTouched && !submitAttempted) {
            return null;
        }
        if (dateOfBirthErrState === 'empty') {
            return translate('enter_your_dob');
        }
        if (dateOfBirthErrState === 'invalid') {
            return translate('check_dob_try_again');
        }
        return null;
    };

    const onInvalidFields = (fields: InvalidField<ForgotUsernameFields>[]) => {
        fields.forEach(f => {
            if (f.name === 'mbi') {
                setMbiErrState('invalid');
            }
            if (f.name === 'lastName') {
                setLastNameErrState('invalid');
            }
            if (f.name === 'dateOfBirth') {
                setDateOfBirthErrState('invalid');
            }
        });
    }
    const header = props.titleText || translate('get_username');
    const enterYourInfo = (
        <>
            <MedicareNumberDialog
                isOpen={dialogIsOpen}
                onExit={() => setDialogIsOpen(false)}
            />
            <Div
                isContainer={true}
                paddingySM={1} paddingyMD={2} paddingyLG={3} paddingyXL={4}
            >
                <fieldset style={{
                    border: 0,
                    margin: 0,
                    padding: 0,
                }}>
                    <Div isRow>
                        <Div cols={12}>
                            <H1 className='ds-u-padding-bottom--3'>{props.titleText || translate('get_username')}</H1>
                            {props.subTitle &&
                                <Div paddingBottom={3}>
                                    <div>{props.subTitle}</div>
                                </Div>
                            }
                            {props.combinedWithPassword &&
                                <Div paddingBottom={3}>
                                    <StepHeader
                                        currentStep={1}
                                        numSteps={4}
                                        stepTitle={translate('enter_your_information')}
                                    />
                                </Div>
                            }
                            <AllFieldsRequired />
                            <SmartErrorAlert
                                name={isIntermediateConversionAttempt ? 'ForgotUsernameAndPassword' : 'ForgotUsername'}
                                apiResponse={apiResponse}
                                mbiRef={medicareNumberRef}
                                lastNameRef={lastNameRef}
                                dateOfBirthRef={monthRef}
                                conversion={conversion}
                                invalidFields={invalidFields}
                                onInvalidField={onInvalidFields}
                                parentHeading={header}
                            />
                        </Div>
                    </Div>
                    <Div isRow>
                        <Div cols={12} colsMD={8}>
                            <MedicareNumberSection
                                errorMessage={getMedicareErrorMessage()}
                                inputRef={setMedicareNumberRef}
                                onBlur={() => setMbiTouched(true)}
                                onChange={updateMedicareNumber}
                                onClick={formStart}
                                onFocus={formStart}
                                value={medicareNumber}
                                parentComponentHeading={header}
                            />
                        </Div>
                    </Div>
                    <LastNameSection
                        errorMessage={getLastNameErrorMessage()}
                        inputRef={setLastNameRef}
                        lastNameVal={lastName}
                        onLastNameBlur={() => setLastNameTouched(true)}
                        onLastNameChange={updateLastName}
                        onSuffixChange={setSuffix}
                        suffixVal={suffix}
                        onLastNameClick={formStart}
                        onLastNameFocus={formStart}
                    />
                    <Div isRow>
                        <Div cols={12}>
                            <DateFields2
                                date={dateOfBirth}
                                hint={translate('use_format_mmddyyyy')}
                                label={translate('date_of_birth')}
                                monthFieldRef={setMonthRef}
                                onComponentBlur={() => setDateOfBirthTouched(true)}
                                onChange={updateDateOfBirth}
                                errorMessage={getDateOfBirthErrMessage()}
                            />
                        </Div>
                    </Div>
                </fieldset>
                <StackingButtons
                    button1={<CancelButton onClick={() => history.push('/login')} />}
                    button2={getGetUsernameButton()}
                />
            </Div>
        </>
    );
    const yourUsername = (
        <Div isContainer={true}>
            <Div isRow>
                <Div cols={12}>
                    <H1 className='ds-u-padding-bottom--3'>{props.combinedWithPassword ? translate('forgot_username_and_password') : translate('your_username')}</H1>
                </Div>
            </Div>
            {props.subTitle &&
                <Div paddingBottom={3}>{props.subTitle}</Div>
            }
            {props.combinedWithPassword &&
                <Div paddingBottom={3}>
                    <StepHeader
                        currentStep={2}
                        numSteps={4}
                        stepTitle={translate('username_reminder')}
                    />
                </Div>
            }
            {props.getSecretQuestionErrorMessage &&
                <Div isRow>
                    <Div cols={12} colsMD={8}>
                        <Alert
                            role={'alert'}
                            variation={'error'}
                        >
                            {props.getSecretQuestionErrorMessage}
                        </Alert>
                    </Div>
                </Div>}
            <div className={'ds-u-margin-bottom--3'}>
                {translate('your_username_is')} <span className='ds-u-font-weight--semibold'>{username}</span>
                <span className='unselectable-full-stop'></span>
            </div>
            {getYourUsernameButtons()}
        </Div>
    );
    const hasUsername = Boolean(username);
    const stepNum = hasUsername ? 2 : 1;
    const stepTitle = hasUsername ? 'Username reminder' : 'Enter your information';
    usePageViewWithSteps(props.titleText || translate('get_username'), stepNum, stepTitle);

    if (isNotFound){
        return <NoAccountInRecords parentName={translate('get_username')}/>;
    } else if (hasUsername) {
        return yourUsername;
    } else {
        return enterYourInfo;
    }
};

export default ForgotUsername;
